import { PriceType } from '@cast/shared-types';

import { useCostOverTime } from 'features/cost-report/hooks/useCostOverTime';
import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import { preparePodCount } from 'features/cost-report/utils/costOverTime';
import { getDailyAverageMetrics } from 'features/cost-report/utils/metrics/getDailyAverageMetrics';

import {
  CostReportChartDataByPeriod,
  CostReportChartType,
  isWorkloadReport,
  PodCountCostChartData,
} from '../../../../../../../cost-report/types/costOverTime';

type UsePodCountChartData = {
  isLoading: boolean;
  data: PodCountCostChartData[];
};

export const usePodCountChartData = (): UsePodCountChartData => {
  const { chartType, timezone } = useCostReportContext();
  const { current, timeSeriesStep } = useCostOverTime();

  const dataPoints =
    chartType === CostReportChartType.BAR && timeSeriesStep !== PriceType.DAILY
      ? getDailyAverageMetrics(
          current.chartData,
          {
            includeGenerated: false,
            includeForecasted: true,
          },
          timezone
        )
      : current.chartData;

  const data = dataPoints.map(
    (datapoint: CostReportChartDataByPeriod): PodCountCostChartData => {
      if (isWorkloadReport(datapoint)) {
        const {
          timestamp,
          onDemandPodCount,
          fallbackPodCount,
          spotPodCount,
          forecast: isForecast,
        } = datapoint;

        const { total, onDemand, fallback, spot } = preparePodCount(
          onDemandPodCount,
          fallbackPodCount,
          spotPodCount
        );

        return {
          timestamp,
          total,
          onDemand,
          fallback: isForecast ? 0 : fallback,
          spot,
          forecast: isForecast,
        };
      } else {
        return {
          timestamp: datapoint.timestamp,
          total: null,
          onDemand: null,
          fallback: null,
          spot: null,
          forecast: datapoint.forecast,
        };
      }
    }
  );

  return {
    isLoading: current.isLoading,
    data,
  };
};
